import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import { asArray } from "@/lib/asArray";
import React, { useEffect, useMemo, useState } from "react";
import useEffectEvent from "@/lib/useEffectEvent";   // Step 7c · E3: the effect runs on its own deps only (see the file)
import api, { toErrMsg } from "@/lib/api";
import { useEngagement } from "@/contexts/EngagementContext";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, Plus, Check, X, Library } from "lucide-react";
import AddFromLibraryDialog from "@/components/AddFromLibraryDialog";
import Ref from "@/components/Ref";

// Batch 0 corrections round 3 — Document Library grouped by 11 stages
// + Governance & Quality (spec sheet 6.1). Forms carry `stage`,
// `defaultLocation`, `engagementTypes`, `reviewDefault`, `standardsBasis`
// in forms_library.json so every column and every filter is data-driven.
const STAGE_ORDER = [
  "Acceptance & Continuance",
  "Planning",
  "Risk Assessment",
  "Audit Response",
  "Controls",
  "Fieldwork",
  "Completion",
  "Reporting",
  "Close & Archive",
  "Engagement-type forms",
  "Governance & Quality",
];
const ENGAGEMENT_TYPE_LABEL = {
  audit: "Audit",
  review: "Review",
  interim_review: "Interim Review",
  special_purpose: "Special Purpose",
  single_statement: "Single Statement",
  summary_fs: "Summary FS",
};
// Round 24c · M2 — coming-soon types hidden from filters/lists (flag only).
const HIDDEN_ENGAGEMENT_TYPES = new Set(["interim_review", "special_purpose", "single_statement", "summary_fs"]);
const VISIBLE_TYPE_ENTRIES = Object.entries(ENGAGEMENT_TYPE_LABEL).filter(([k]) => !HIDDEN_ENGAGEMENT_TYPES.has(k));

function refKey(ref) {
  if (!ref) return [99999, ""];
  const m = String(ref).trim().match(/^(\d+)(.*)$/);
  // Batch 20 · A4 (F-140): a revised version reads "101 (a) revised 2025" — the space before "(a)" must not sort it ahead of
  // "101(a)"; the suffix is compared without its leading space, so the revised version sits right after the current one.
  if (m) return [parseInt(m[1], 10), (m[2] || "").trim().toLowerCase()];
  return [99998, String(ref).toLowerCase()];
}

export default function DocumentLibrary() {
  const colW = useResizableColumns({ storageKey: "document-library", defaults: [110, 240, 160, 160, 160, 110], mins: [72, 130, 100, 100, 100, 72] });   // Step 11d · add-on
  const { t } = useI18n();
  const { currentEngId } = useEngagement();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [pendingAddId, setPendingAddId] = useState(null);
  const [popupOpen, setPopupOpen] = useState(false);
  const [stageFilter, setStageFilter] = useState("__all__");
  const [typeFilter, setTypeFilter] = useState("__all__");

  const load = async () => {
    if (!currentEngId) return;
    try {
      const r = await api.get(`/forms/${currentEngId}/document-library`);
      setData(r.data);
    } catch (e) { toast.error(toErrMsg(e, t("docLibExtras.failedLoad"))); }
  };
  const loadOnChange = useEffectEvent(load);
  useEffect(() => { loadOnChange(); }, [currentEngId, loadOnChange]);

  const rowsByStage = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    const filtered = asArray(data.forms).filter((f) => {
      if (stageFilter !== "__all__" && f.stage !== stageFilter) return false;
      if (typeFilter !== "__all__" && !(f.engagementTypes || []).includes(typeFilter)) return false;
      if (!needle) return true;
      return (f.ref_no || "").toLowerCase().includes(needle)
          || (f.title || "").toLowerCase().includes(needle)
          || (f.standardsBasis || "").toLowerCase().includes(needle);
    });
    const by = new Map();
    for (const s of STAGE_ORDER) by.set(s, []);
    for (const f of filtered) {
      const s = STAGE_ORDER.includes(f.stage) ? f.stage : "Planning";
      by.get(s).push(f);
    }
    for (const arr of by.values()) {
      arr.sort((a, b) => {
        const [an, as] = refKey(a.ref_no);
        const [bn, bs] = refKey(b.ref_no);
        if (an !== bn) return an - bn;
        return as < bs ? -1 : 1;
      });
    }
    return STAGE_ORDER
      .map((s) => ({ stage: s, forms: by.get(s) || [] }))
      .filter((g) => g.forms.length > 0);
  }, [data, q, stageFilter, typeFilter]);

  const addToEngagement = async (form) => {
    setPendingAddId(form.form_id);
    try {
      await api.post(`/forms/${currentEngId}/${form.form_id}/add-to-engagement`);
      toast.success(t("docLibExtras.addedToEng").replace("{ref}", form.ref_no));
      await load();
    } catch (e) {
      toast.error(toErrMsg(e, t("docLibExtras.failedAdd")));
    } finally { setPendingAddId(null); }
  };

  if (!currentEngId) {
    return (
      <div className="p-6" data-testid="doclib-page">
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">
          {t("docLib.selectEng")}
        </CardContent></Card>
      </div>
    );
  }
  if (!data) {
    return <div className="p-6 text-sm text-muted-foreground">{t("docLib.loading")}</div>;
  }

  const totalIn = asArray(data.forms).filter((f) => f.in_engagement).length;
  const totalAvail = (data.forms || []).length - totalIn;
  const stageLbl = (s) => t(`docLibStages.${s}`) || s;
  const typeLbl = (k) => ENGAGEMENT_TYPE_LABEL[k] || k;

  return (
    <div className="space-y-5" data-testid="doclib-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Library className="h-5 w-5" /> {t("docLibExtras.title")}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t("docLibExtras.overview")
              .replace("{total}", (data.forms || []).length)
              .replace("{inEng}", totalIn)
              .replace("{avail}", totalAvail)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setPopupOpen(true)}
                  data-testid="doclib-open-popup">
            <Library className="h-3.5 w-3.5 me-1.5" /> {t("addFromLibrary.title")}
          </Button>
          <Select value={stageFilter} onValueChange={setStageFilter}>
            <SelectTrigger className="h-9 w-52" data-testid="doclib-stage-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">{t("docLibHeaders.allStages")}</SelectItem>
              {STAGE_ORDER.map((s) => (
                <SelectItem key={s} value={s} data-testid={`doclib-stage-opt-${s.replace(/\s+/g,'-')}`}>
                  {stageLbl(s)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="h-9 w-48" data-testid="doclib-type-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">{t("docLibHeaders.allTypes")}</SelectItem>
              {VISIBLE_TYPE_ENTRIES.map(([k, v]) => (
                <SelectItem key={k} value={k} data-testid={`doclib-type-opt-${k}`}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative">
            <Search className="pointer-events-none absolute start-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("docLib.searchPlaceholder")}
              className="h-9 w-72 ps-7 pe-6 text-sm"
              data-testid="doclib-search"
            />
            {q && (
              <button onClick={() => setQ("")}
                      className="absolute end-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                      data-testid="doclib-search-clear">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
      <AddFromLibraryDialog open={popupOpen} onOpenChange={setPopupOpen} onAdded={load} />

      {rowsByStage.length === 0 && (
        <Card><CardContent className="p-8 text-center text-sm text-muted-foreground"
                              data-testid="doclib-no-match">
          {t("docLibExtras.noMatch").replace("{q}", q)}
        </CardContent></Card>
      )}

      {rowsByStage.map((g) => (
        <Card key={g.stage} data-testid={`doclib-stage-${g.stage.replace(/\s+/g,'-')}`}>
          <CardHeader className="py-3">
            <CardTitle className="text-base flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span>{stageLbl(g.stage)}</span>
                <Badge variant="outline" className="text-[10px]">{g.forms.length}</Badge>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <ResizableTable cols={colW} className="w-full text-sm">
              <thead className="border-b bg-slate-50 dark:bg-slate-900/40 text-[11px] uppercase text-muted-foreground">
                <tr>
                  <ResizableTh index={0} className="px-3 py-2 text-start w-20">{t("docLibHeaders.ref")}</ResizableTh>
                  <ResizableTh index={1} className="px-3 py-2 text-start">{t("docLibHeaders.title")}</ResizableTh>
                  <ResizableTh index={2} className="px-3 py-2 text-start">{t("docLibHeaders.isa")}</ResizableTh>
                  <ResizableTh index={3} className="px-3 py-2 text-start">{t("docLibHeaders.defaultLocation")}</ResizableTh>
                  <ResizableTh index={4} className="px-3 py-2 text-start">{t("docLibHeaders.engagementTypes")}</ResizableTh>
                  <ResizableTh index={5} className="px-3 py-2 text-start w-40">{t("docLibHeaders.status")}</ResizableTh>
                </tr>
              </thead>
              <tbody className="divide-y">
                {g.forms.map((f) => (
                  <tr key={f.form_id} data-testid={`doclib-row-${f.form_id}`}>
                    <td className="px-3 py-2 font-mono text-[11px] text-slate-700 dark:text-slate-300">
                      <Ref>{f.ref_no}</Ref>
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{f.title}</div>
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">
                      {f.standardsBasis || "—"}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {f.defaultLocation || "—"}
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {asArray(f.engagementTypes).filter((k) => !HIDDEN_ENGAGEMENT_TYPES.has(k)).map((k) => (
                          <Badge key={k} variant="outline" className="text-[10px]">{typeLbl(k)}</Badge>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {f.in_engagement ? (
                        <Badge variant="outline" className="gap-1 text-[10px]"
                                data-testid={`doclib-in-${f.form_id}`}>
                          <Check className="h-3 w-3" /> {t("docLibExtras.inEngagement")}
                        </Badge>
                      ) : (
                        <Button size="sm" variant="outline" className="h-7 gap-1"
                                disabled={pendingAddId === f.form_id}
                                onClick={() => addToEngagement(f)}
                                data-testid={`doclib-add-${f.form_id}`}>
                          <Plus className="h-3 w-3" />
                          {pendingAddId === f.form_id ? t("docLib.adding") : t("docLibExtras.addToEng")}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </ResizableTable>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// Legacy compatibility export for FormPermanentDeleteDialog imported by Forms.jsx.
export function FormPermanentDeleteDialog(props) { return null; }
